import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Users, FileText, CalendarDays, ArrowRight, AlertCircle, SearchX } from 'lucide-react'
import { apiJson } from '../api'

interface SearchHit {
  id: string
  title: string
  subtitle: string
  status?: string
}

interface GroupedSearchResult {
  employees: { total: number; items: SearchHit[] }
  documents: { total: number; items: SearchHit[] }
  leave: { total: number; items: SearchHit[] }
}

interface GroupDef {
  key: keyof GroupedSearchResult
  label: string
  icon: typeof Users
  tint: string
  to: (q: string) => string
}

const GROUPS: GroupDef[] = [
  { key: 'employees', label: 'Employees', icon: Users, tint: 'bg-blue-50 text-blue-600', to: (q) => `/employees?q=${encodeURIComponent(q)}` },
  { key: 'documents', label: 'Documents', icon: FileText, tint: 'bg-emerald-50 text-emerald-600', to: (q) => `/documents?q=${encodeURIComponent(q)}` },
  { key: 'leave', label: 'Leave requests', icon: CalendarDays, tint: 'bg-amber-50 text-amber-600', to: () => '/leave' },
]

export default function SearchResults() {
  const [params] = useSearchParams()
  const q = params.get('q') ?? ''
  const [result, setResult] = useState<GroupedSearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!q.trim()) {
      setResult(null)
      return
    }
    let cancelled = false
    setLoading(true)
    apiJson<GroupedSearchResult>(`/search?q=${encodeURIComponent(q)}`)
      .then((data) => {
        if (!cancelled) {
          setResult(data)
          setError('')
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Search failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [q])

  const grandTotal = result
    ? GROUPS.reduce((sum, g) => sum + result[g.key].total, 0)
    : 0

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-extrabold tracking-tight sm:text-2xl">
          {q ? `Results for “${q}”` : 'Search'}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {q
            ? loading
              ? 'Searching across the workspace…'
              : result
                ? `${grandTotal} match${grandTotal === 1 ? '' : 'es'} across employees, documents, and leave`
                : ''
            : 'Type in the search bar above to search across the whole workspace.'}
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {loading && (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
        </div>
      )}

      {!loading && result && grandTotal === 0 && q && (
        <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
            <SearchX size={22} />
          </div>
          <p className="mt-3 text-sm font-semibold">No matches</p>
          <p className="mt-1 text-xs text-slate-400">Nothing in the workspace matches “{q}”.</p>
        </div>
      )}

      {!loading &&
        result &&
        GROUPS.map((group) => {
          const data = result[group.key]
          if (data.total === 0) return null
          const Icon = group.icon
          return (
            <div key={group.key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${group.tint}`}>
                    <Icon size={15} />
                  </span>
                  <h3 className="text-sm font-bold tracking-tight">{group.label}</h3>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                    {data.total}
                  </span>
                </div>
                {data.total > data.items.length && (
                  <Link
                    to={group.to(q)}
                    className="flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700"
                  >
                    View all {data.total}
                    <ArrowRight size={13} />
                  </Link>
                )}
              </div>
              <ul className="divide-y divide-slate-100">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={group.to(q)}
                      className="flex items-center gap-3.5 px-5 py-3 transition-colors hover:bg-slate-50/70"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{item.title}</p>
                        <p className="truncate text-xs text-slate-400">{item.subtitle}</p>
                      </div>
                      {item.status && (
                        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-500">
                          {item.status}
                        </span>
                      )}
                      <Search size={13} className="shrink-0 text-slate-300" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
    </div>
  )
}
